import { useCallback } from 'react';
import { Checkbox, Header, Text, VStack } from '@direct-frontend/components';
import { useSearchParams } from '@direct-frontend/modules-router';
import { logEventString } from '@adv-frontend/yastore-modules/logger';
import { useConfirm } from '#shared/lib/hooks';
import { constructorTestIds } from '#shared/lib/testids';
import { constructorProductLoggingEvents } from '../lib/constructorProductLoggingEvents';
import { deleteConstructorSection, getConstructorSectionSettings } from '../model/slices';
import { getConstructorCountChildren } from './useConstructorCountChildren';

export const useConstructorDeleteSection = () => {
    const [searchParams] = useSearchParams();
    const { confirm, renderConfirmModal } = useConfirm();

    const deleteSection = useCallback(
        async (pageId: string, sectionInstanceId: string) => {
            const sectionSettings = getConstructorSectionSettings(pageId, sectionInstanceId);

            if (!sectionSettings) {
                return;
            }

            let deleteAll = false;

            const { childrenCount } = getConstructorCountChildren({ sectionId: sectionSettings.id });

            if (sectionSettings.section_type === 'parent' && childrenCount > 0) {
                const isConfirmed = await confirm({
                    title: null,
                    bodyText: (
                        <VStack gap="6">
                            <Header level="h2" textAlign="left">
                                Удалить компонент?
                            </Header>
                            <Text size="m" role="supplementary" textAlign="left">
                                Чтобы сохранить связку компонента, назначим одну из дочерних секций в качестве
                                родительской.
                            </Text>
                            <Checkbox
                                onChange={(isSelected) => (deleteAll = isSelected)}
                                size="m"
                                data-testid={constructorTestIds.sectionDeleteComponentDeleteAll}
                            >
                                <Text>Удалить все дочерние секции компонента вместе с родительской</Text>
                            </Checkbox>
                        </VStack>
                    ),
                    applyText: 'Удалить',
                    'data-testid': constructorTestIds.sectionDeleteComponentModal,
                    applyButtonTestId: constructorTestIds.sectionDeleteComponentConfirm,
                    cancelButtonTestId: constructorTestIds.sectionDeleteComponentCancel,
                });

                if (!isConfirmed) {
                    return;
                }
            }

            logEventString(constructorProductLoggingEvents.Section.Delete, 'true', {
                pageId,
                sectionInstanceId,
                deleteAll: Boolean(deleteAll),
            });

            searchParams.delete('section');
            searchParams.delete('module');
            deleteConstructorSection(pageId, sectionInstanceId, deleteAll);
        },
        [searchParams, confirm]
    );

    return { deleteSection, renderConfirmModal };
};
